import { create, type StoreApi } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { UseBoundStore } from 'zustand/react';
import type { Side } from '@/common/constants';
import type { ViewPosition } from '@/feature/terminal_tables/position/types';
import { BigIntDevtoolsSerialize, CURRENT_APP_VERSION, clearDataMigration } from '@/utils/zustand_helpers';
import type { SizeUnit } from './header/size_unit_toggle';
import type { SpreadUnit } from './ticker/ticker';

interface OrderFormPrefillData {
  price: number;
  side: Side;
}

interface SharedTradingState {
  sizeUnit: SizeUnit;
  spreadUnit: SpreadUnit;
  prefillData: OrderFormPrefillData | null;
  requestedSide: Side | null;
  selectedPosition: ViewPosition | null;
  leverageByMarket: Record<string, number>;
}

interface SharedTradingActions {
  setSizeUnit: (sizeUnit: SizeUnit) => void;
  toggleSizeUnit: () => void;
  toggleSpreadUnit: () => void;
  setPrefillData: (data: OrderFormPrefillData) => void;
  clearPrefillData: () => void;
  requestSide: (side: Side) => void;
  clearRequestedSide: () => void;
  setSelectedPosition: (position: ViewPosition | null) => void;
  setMarketLeverage: (marketId: number, leverage: number) => void;
}

export type SharedTradingStore = SharedTradingState & SharedTradingActions;

export const useSharedTradingStore: UseBoundStore<StoreApi<SharedTradingStore>> = create<SharedTradingStore>()(
  persist(
    devtools(
      immer((set, get) => ({
        sizeUnit: 'asset',
        spreadUnit: 'percent' as SpreadUnit,
        prefillData: null as OrderFormPrefillData | null,
        requestedSide: null as Side | null,
        selectedPosition: null as ViewPosition | null,
        leverageByMarket: {} as Record<string, number>,

        setSizeUnit: (sizeUnit: SizeUnit) => {
          set((draft) => {
            draft.sizeUnit = sizeUnit;
          });
        },

        toggleSizeUnit: () => {
          set((draft) => {
            draft.sizeUnit = draft.sizeUnit === 'asset' ? 'quote' : 'asset';
          });
        },

        toggleSpreadUnit: () => {
          set((draft) => {
            draft.spreadUnit = draft.spreadUnit === 'percent' ? 'bps' : 'percent';
          });
        },

        setPrefillData: (data: OrderFormPrefillData) => {
          set((draft) => {
            draft.prefillData = data;
          });
        },

        clearPrefillData: () => {
          set((draft) => {
            draft.prefillData = null;
          });
        },

        requestSide: (side: Side) => {
          set((draft) => {
            draft.requestedSide = side;
          });
        },

        clearRequestedSide: () => {
          set((draft) => {
            draft.requestedSide = null;
          });
        },

        setSelectedPosition: (position: ViewPosition | null) => {
          set((draft) => {
            draft.selectedPosition = position;
          });
        },

        setMarketLeverage: (marketId: number, leverage: number) => {
          set((draft) => {
            draft.leverageByMarket[String(marketId)] = leverage;
          });
        },
      })),
      {
        name: 'dex-shared-trading',
        ...BigIntDevtoolsSerialize,
      }
    ),
    {
      name: 'dex-shared-trading',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sizeUnit: state.sizeUnit,
        spreadUnit: state.spreadUnit,
        leverageByMarket: state.leverageByMarket,
      }),
    }
  )
);
